import React, { useCallback, useEffect, useState } from 'react';
import { GeoLog } from '../types';
import { getGeoLogs, GeoLogFiltros } from '../services/geologs.service';
import { formatDateTimeAr24 } from '../utils/dateTime';

const RegistrosPage: React.FC = () => {
  const [logs, setLogs] = useState<GeoLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filtros, setFiltros] = useState<GeoLogFiltros>({});
  const [tempFiltros, setTempFiltros] = useState<GeoLogFiltros>({});

  const cargar = useCallback(async (f?: GeoLogFiltros, targetPage = page) => {
    setLoading(true);
    try {
      const res = await getGeoLogs({ ...(f || filtros), page: targetPage, limit });
      setLogs(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      setPage(res.data.page || targetPage);
    } catch {}
    setLoading(false);
  }, [filtros, limit, page]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const nextDni = tempFiltros.dni?.trim() || undefined;
    const currentDni = filtros.dni?.trim() || undefined;
    if (nextDni === currentDni) return;

    const timeout = setTimeout(() => {
      const nextFiltros = { ...filtros, dni: nextDni };
      setFiltros(nextFiltros);
      setPage(1);
      cargar(nextFiltros, 1);
    }, 300);

    return () => clearTimeout(timeout);
  }, [tempFiltros.dni, filtros, cargar]);

  const handleAplicar = () => {
    setFiltros(tempFiltros);
    setPage(1);
    cargar(tempFiltros, 1);
  };

  const handleLimpiar = () => {
    setTempFiltros({});
    setFiltros({});
    setPage(1);
    cargar({}, 1);
  };

  const getAgenteName = (agente: any) =>
    typeof agente === 'object' ? agente.nombre : agente;

  const getAgenteDoc = (agente: any) =>
    typeof agente === 'object' ? agente.dni : '-';

  const getSitioName = (sitio: any) =>
    typeof sitio === 'object' ? sitio.nombre : sitio;

  const formatFecha = (d: string) => {
    return formatDateTimeAr24(d);
  };

  const estadoBadge = (log: GeoLog) => {
    if (log.tipo === 'Intento' || log.tipo === 'Forwarded' || !log.valido) {
      return (
        <>
          <span className="badge badge-danger">Rechazado</span>
          {log.reglaRechazo && (
            <span className="badge badge-secondary" style={{ marginLeft: '6px' }}>
              {log.reglaRechazo}
            </span>
          )}
        </>
      );
    }
    return <span className="badge badge-success">Aprobado</span>;
  };

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    cargar(filtros, nextPage);
  };

  return (
    <>
      <h1 className="page-title">Geo Asistencia - Registros</h1>

      {/* Filtros */}
      <div className="card">
        <h2 className="card-title">Filtros</h2>
        <div className="filters-grid">
          <div className="form-group">
            <label>Tipo</label>
            <select
              value={tempFiltros.tipo || ''}
              onChange={e => setTempFiltros(p => ({ ...p, tipo: e.target.value || undefined }))}
            >
              <option value="">Todos</option>
              <option value="Entrada">Entrada</option>
              <option value="Salida">Salida</option>
              <option value="Intento">Intento</option>
            </select>
          </div>
          <div className="form-group">
            <label>Validacion</label>
            <select
              value={tempFiltros.valido || ''}
              onChange={e => setTempFiltros(p => ({ ...p, valido: e.target.value || undefined }))}
            >
              <option value="">Todos</option>
              <option value="true">Aprobado</option>
              <option value="false">Rechazado</option>
            </select>
          </div>
          <div className="form-group">
            <label>Agente por DNI</label>
            <input
              value={tempFiltros.dni || ''}
              onChange={e => setTempFiltros(p => ({ ...p, dni: e.target.value || undefined }))}
              placeholder="Buscar por DNI"
            />
          </div>
          <div className="form-group">
            <label>Desde</label>
            <input
              type="date"
              value={tempFiltros.desde || ''}
              onChange={e => setTempFiltros(p => ({ ...p, desde: e.target.value || undefined }))}
            />
          </div>
          <div className="form-group">
            <label>Hasta</label>
            <input
              type="date"
              value={tempFiltros.hasta || ''}
              onChange={e => setTempFiltros(p => ({ ...p, hasta: e.target.value || undefined }))}
            />
          </div>
        </div>
        <div className="filter-actions">
          <button className="btn btn-primary btn-sm" onClick={handleAplicar}>Aplicar</button>
          <button className="btn btn-outline btn-sm" onClick={handleLimpiar}>Limpiar</button>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        <h2 className="card-title">Tabla de marcaciones</h2>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : logs.length === 0 ? (
          <div className="empty-state">No hay registros que coincidan con los filtros.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Agente</th>
                  <th>DNI</th>
                  <th>Tipo</th>
                  <th>Fecha y hora</th>
                  <th>Sitio</th>
                  <th>Mensaje</th>
                  <th>Ubicacion</th>
                  <th>Distancia</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log._id}>
                    <td>{getAgenteName(log.agente)}</td>
                    <td>{getAgenteDoc(log.agente)}</td>
                    <td>{log.tipo}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatFecha(log.createdAt)}</td>
                    <td>{getSitioName(log.sitio)}</td>
                    <td>
                      {log.mensaje
                        ? <span style={{ color: '#3498db', fontSize: '0.85rem' }}>{log.mensaje}</span>
                        : '-'}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {log.latitud != null && log.longitud != null
                        ? `${log.latitud}, ${log.longitud}`
                        : 'Sin coordenadas'}
                    </td>
                    <td>{log.distancia != null ? `${log.distancia}m` : '-'}</td>
                    <td>{estadoBadge(log)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px' }}>
          <span style={{ fontSize: '0.9rem', color: '#555' }}>
            Total: {total} | Pagina {page} de {totalPages}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
              Anterior
            </button>
            <button
              className="btn btn-outline btn-sm"
              disabled={page >= totalPages}
              onClick={() => goToPage(page + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegistrosPage;
